import React, { useEffect, useRef } from 'react';
import Section from './Section';
import { CONTACT_EMAIL, PHONE_NUMBER } from '../constants';
import MailIcon from './icons/MailIcon'; // Assuming you'll create/have this
import PhoneIcon from './icons/PhoneIcon'; // Assuming you'll create/have this

const ContactSection: React.FC = () => {
  const cognitoFormContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (cognitoFormContainerRef.current) {
      // Check if a script from cognitoforms.com is already in the container
      // This is to prevent adding multiple scripts during development (e.g., with HMR)
      // or if the component re-renders for other reasons.
      const existingScript = cognitoFormContainerRef.current.querySelector(
        'script[src^="https://www.cognitoforms.com"]'
      );
      if (existingScript) {
        // Potentially remove and re-add, or trust Cognito's script to handle it.
        // For now, let's assume if it's there, Cognito has handled it or will.
        // If issues arise with form not loading on re-renders, this logic might need adjustment.
        // A more robust way might be to ensure Cognito's cleanup if their script supports it.
        return;
      }

      const script = document.createElement('script');
      script.src = "https://www.cognitoforms.com/f/seamless.js";
      script.setAttribute('data-key', "Vy03wziPekyukWndHXJsBg");
      script.setAttribute('data-form', "2");
      script.async = true;
      
      cognitoFormContainerRef.current.appendChild(script);
    }
    // No explicit cleanup for the script itself, as Cognito's seamless.js manages the iframe/form content.
    // Removing the script tag after it has run might not remove the form.
    // The container div (cognitoFormContainerRef.current) will be removed when the component unmounts.
  }, []); // Empty dependency array ensures this runs once after initial render.

  return (
    <Section id="contact" title="Contact Us" className="bg-white" titleAccent="text-emerald-700">
      <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-start">
        {/* Contact Information */}
        <div className="space-y-8">
          <div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">Contact Information</h3>
            <p className="text-lg text-gray-600 mb-6">
              We're here to help! Whether you have questions about our labels, need a custom quote, or want to place an order, feel free to reach out.
            </p>
            <div className="space-y-4">
              <div className="flex items-center text-lg">
                <MailIcon className="w-6 h-6 text-sky-500 mr-3 flex-shrink-0" />
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-gray-700 hover:text-sky-600 transition-colors">
                  {CONTACT_EMAIL}
                </a>
              </div>
              <div className="flex items-center text-lg">
                <PhoneIcon className="w-6 h-6 text-sky-500 mr-3 flex-shrink-0" />
                <a href={`tel:${PHONE_NUMBER}`} className="text-gray-700 hover:text-sky-600 transition-colors">
                  {PHONE_NUMBER}
                </a>
              </div>
            </div>
          </div>
          <div>
             <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">Office Hours</h3>
             <p className="text-lg text-gray-600">
                Monday - Friday: 9:00 AM - 5:00 PM (PST)
             </p>
             <p className="text-lg text-gray-600">
                Closed on weekends and public holidays.
             </p>
          </div>
        </div>


        {/* Cognito Contact Form Container */}
        <div className="bg-gray-50 p-8 rounded-xl shadow-lg border border-gray-200">
          <h3 className="text-2xl font-semibold text-gray-800 mb-6">Send Us a Message</h3>
          <div ref={cognitoFormContainerRef} id="cognito-form-placeholder">
            {/* The Cognito Forms script will populate this area */}
          </div>
        </div>
      </div>
    </Section>
  );
};

export default ContactSection;